import { useEffect, useState } from "react";
import { BalanceAdjustmentHistoryDetails } from "../../models/response/balanceAdjustmentListResponse";
import { getAdjustmentHistory } from "../../services/transactions.service";
import { ApiDateFormatRequest } from "../../../../models/Request/apiDateFormatRequest";
import { formatUtcToLocalDateTime } from "../../../../utils/dateTimeHelper";
import { ADJUSTMENTS_STATUS_STYLE } from "../../models/constants/statusStyles";
import TransactionCard from "../../../../components/ui/TransactionCard";
import TransactionFilter from "./TransactionFilters";
import localization from "../../../../config/localization";
import InfiniteScroll from "../../../../components/ui/InfiniteScroll";
import common from "../../../../utils/common";
import { useAppDispatch } from "../../../../store/hooks";
import { changeLoaderState } from "../../../../reducers/appStateReducer";

export default function Adjustments() {
  const dispatch = useAppDispatch();
  type Filters = {
    searchText: string | null;
    fromDate: string | null;
    toDate: string | null;
    statuses: string[];
  };

  const Default_Filters: Filters = {
    searchText: null,
    fromDate: null,
    toDate: null,
    statuses: [] as string[],
  };

  const [loading, setLoading] = useState(true);
  const [openRowIds, setOpenRowIds] = useState<string[]>([]);
  const [data, setItems] = useState<BalanceAdjustmentHistoryDetails[]>([]);
  const [pageNo, setPageNo] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [filters, setFilters] = useState<Filters>(Default_Filters);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const handleCardToggle = (itemId: string, isOpen: boolean) => {
    setOpenRowIds((prev) => {
      if (isOpen) {
        if (!prev.includes(itemId)) {
          return [...prev, itemId];
        }
        return prev;
      } else {
        return prev.filter((id) => id !== itemId);
      }
    });
  };

  function createAdjustmentHistoryRequest(
    pageNo: number,
    filters: {
      searchText: string | null;
      fromDate: string | null;
      toDate: string | null;
      statuses: string[];
    },
  ): ApiDateFormatRequest {
    return {
      pageNo: pageNo,
      pageSize: 20,
      fromDate: filters.fromDate,
      toDate: filters.toDate,
      transactionId: filters.searchText?.trim() || null,
      status: filters.statuses.length ? filters.statuses.join(",") : null,
      isCallFromMobileDevice: false,
    };
  }

  const handleFiltersChange = (updated: Partial<typeof filters>) => {
    setFilters((prev) => ({ ...prev, ...updated }));
  };

  const fetchData = async (isReset = false, appliedFilters = filters) => {
    if (isLoadingMore && !isReset) {
      return;
    }

    if (isReset) {
      setLoading(true);
      dispatch(changeLoaderState(true));
    } else {
      setIsLoadingMore(true);
    }

    try {
      const currentPageNo = isReset ? 0 : pageNo;
      const request = createAdjustmentHistoryRequest(
        currentPageNo,
        appliedFilters,
      );
      const newData = await getAdjustmentHistory(request);
      if (newData) {
        setItems((prev) => {
          if (isReset || !prev) {
            return newData;
          }
          return [...prev, ...newData];
        });
      }

      if (!newData || newData.length < 20) {
        setHasMore(false);
      }
      setPageNo((prev) => (isReset ? 1 : prev + 1));
    } finally {
      if (isReset) {
        setLoading(false);
        dispatch(changeLoaderState(false));
      } else {
        setIsLoadingMore(false);
      }
    }
  };

  const resetPagination = () => {
    setItems([]);
    setPageNo(0);
    setHasMore(true);

    // Reset UI state for open cards.
    setOpenRowIds([]);
  };

  const handleApply = async (appliedFilters: {
    searchText: string | null;
    fromDate: string | null;
    toDate: string | null;
    statuses: string[];
  }) => {
    setFilters(appliedFilters);
    resetPagination();

    await fetchData(true, appliedFilters);
  };

  useEffect(() => {
    const init = async () => {
      await fetchData(true);
    };
    init();
  }, []);

  return (
    <div>
      <div>
        <TransactionFilter
          filters={filters}
          onFiltersChange={handleFiltersChange}
          statusOptions={[]}
          onApply={handleApply}
        />
      </div>

      <InfiniteScroll
        onLoadMore={() => fetchData()}
        hasMore={hasMore}
        isLoading={loading || isLoadingMore}
      >
        {!loading && data?.length === 0 && (
          <div className="text-xl flex justify-center mt-1">
            {localization.no_balance_adjustment}
          </div>
        )}
        <div className="grid grid-cols-1 md:landscape:grid-cols-2 lg:portrait:grid-cols-1 lg:landscape:grid-cols-2 xl:grid-cols-2 gap-4">
          {data?.map((item) => (
            <div key={item.balanceadjustmentid}>
              <TransactionCard
                onToggleChange={(isOpen) =>
                  handleCardToggle(item.balanceadjustmentid, isOpen)
                }
              >
                <div className="grid grid-rows-2 gap-2 text-white">
                    <div>
                      <span className="font-semibold text-sm md:landscape:text-xs lg:landscape:text-xs lg:portrait:text-base xl:landscape:text-base text-[#ECECFF] tracking-wide">
                        {item.balanceadjustmentid}
                        {openRowIds.includes(item.balanceadjustmentid) && (
                          <img
                            src={"/icons/ic-copy.svg"}
                            alt="Copy"
                            className="size-4 inline ml-2 cursor-pointer"
                            onClick={() =>
                              common.copyIdToClipboard(
                                item.balanceadjustmentid,
                                localization.transid_success_copied,
                              )
                            }
                          />
                        )}
                      </span>
                      <span
                        className={`float-right font-medium p-1 text-xs md:portrait:text-sm md:landscape:text-xs lg:landscape:text-xs lg:portrait:text-sm xl:landscape:text-sm rounded-sm ${ADJUSTMENTS_STATUS_STYLE[item.adjustmenttype]
                          }`}
                      >
                        {localization.getString(
                          item.adjustmenttype.toLowerCase(),
                        )}
                      </span>
                    </div>

                    <div className="font-medium text-sm text-[#A9A9A9]">
                      <span>{formatUtcToLocalDateTime(item.created)}</span>
                      <span className="float-right text-[#E6E6E6]">
                        {`${item.amount.toFixed(2)}\u20B9`}
                      </span>
                    </div>

                    {openRowIds.includes(item.balanceadjustmentid) && (
                      <div className="grid grid-rows-2 gap-2 border-t border-[#353535] mt-2 pt-2 font-medium text-sm text-[#A9A9A9]">
                        <div>
                          <span>{localization.balance_type}</span>
                          <span className="float-right text-white">
                            {item?.wallettype}
                          </span>
                        </div>

                        <div>
                          <span>{localization.remark}</span>
                          <span className="float-right text-white">
                            {item?.remarks}
                          </span>
                        </div>
                      </div>
                    )}
                </div>
              </TransactionCard>
            </div>
          ))}
        </div>
      </InfiniteScroll>
    </div>
  );
}
